import { useLazyQuery } from '@apollo/client'

import { JsonConfigurationEntityType } from '@flowwai-frontend/bps-officer-web/graphql'
import { APIStatus } from '@flowwai-frontend/shared/api-common'

import CustomError from '../../models/CustomError'
import {
   convertToErrorType,
   getAPIStatusFromNetworkStatus
} from '../../utils/APIUtils'
import {
   GetEntityJsonConfigurationDocument as Document,
   GetEntityJsonConfigurationQuery as Query,
   GetEntityJsonConfigurationQueryVariables as QueryVariables
} from './query.generatedTypes'
import useResponseHandler from './useResponseHandler'

export interface FailureArgsType {
   error?: Error | null
   data?: CustomError<Query['getEntityJsonConfiguration']>
}

interface APIArgs {
   entityId?: string | null
   entityType: JsonConfigurationEntityType
   onSuccess?: (jsonData: string) => void
   onFailure?: (args: FailureArgsType) => void
}

interface ReturnType {
   apiError: Error | null
   apiStatus: APIStatus
   triggerApi: (args: APIArgs) => void
}

const useGetEntityJsonConfig = (): ReturnType => {
   const [getEntityJsonConfig, { networkStatus, data, error }] = useLazyQuery<
      Query,
      QueryVariables
   >(Document)

   const { handleAPIResponse } = useResponseHandler()

   const triggerApi = (args: APIArgs) => {
      getEntityJsonConfig({
         variables: {
            params: {
               entityId: args.entityId,
               entityType: args.entityType
            }
         },
         fetchPolicy: 'cache-first',
         onCompleted: data => {
            handleAPIResponse({
               data: data,
               onSuccess: args.onSuccess,
               onFailure: (
                  errorMessage: string,
                  data: Query['getEntityJsonConfiguration']
               ): void =>
                  args.onFailure?.({
                     data: new CustomError(errorMessage, data)
                  })
            })
         },
         onError: error => {
            args.onFailure?.({
               error
            })
         }
      })
   }

   const apiError = convertToErrorType(error)

   const apiStatus = getAPIStatusFromNetworkStatus(networkStatus, data)

   return {
      apiError,
      apiStatus,
      triggerApi
   }
}

export default useGetEntityJsonConfig
